import React, { useCallback, useEffect, useState } from "react";
import { getStoredTokens, clearTokens } from "../api/auth";
import Login from "./components/Login";
import PlayBrowser from "./components/PlayBrowser";
import MaskPanel from "./components/MaskPanel";
import { getSelectedChlkPlay, SelectedChlkPlay } from "./office/chlkShape";
import "./styles/taskpane.css";

type Screen = "login" | "browse" | "mask";

function isMaskModeUrl(): boolean {
  return new URLSearchParams(window.location.search).get("mode") === "mask";
}

const MASK_ERROR_NO_PLAY =
  "No CHLK play found. Select a play inserted with CHLK (click Insert), then try Zoom Play again.";
const MASK_ERROR_UNTAGGED =
  "This image wasn't inserted by CHLK. Insert the play again with the Insert button, then use Zoom Play.";

export default function App() {
  const [screen, setScreen] = useState<Screen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [maskSelection, setMaskSelection] = useState<SelectedChlkPlay | null>(null);

  const openMaskEditor = useCallback(async () => {
    try {
      const selected = await getSelectedChlkPlay();
      if (selected) {
        setMaskSelection(selected);
        setScreen("mask");
        setError(null);
        return true;
      }
      setError(isMaskModeUrl() ? MASK_ERROR_NO_PLAY : MASK_ERROR_UNTAGGED);
      return false;
    } catch (err) {
      console.error("[CHLK mask open]", err);
      setError("Could not read the selected play.");
      return false;
    }
  }, []);

  useEffect(() => {
    const AUTH_CHECK_TIMEOUT_MS = 3_000;
    const deadline = new Promise<null>((resolve) =>
      window.setTimeout(() => resolve(null), AUTH_CHECK_TIMEOUT_MS)
    );
    Promise.race([getStoredTokens(), deadline])
      .then(async (tokens) => {
        if (!tokens) {
          setScreen("login");
          return;
        }
        if (isMaskModeUrl()) {
          const opened = await openMaskEditor();
          setScreen(opened ? "mask" : "browse");
        } else {
          setScreen("browse");
        }
      })
      .catch((err) => {
        console.error("[CHLK startup error]", err);
        setError("We couldn't restore your session. Please sign in again.");
        setScreen("login");
      });
  }, [openMaskEditor]);

  function handleLogout() {
    clearTokens().then(() => setScreen("login"));
  }

  function handleAuthExpired() {
    clearTokens().then(() => {
      setError("Your session expired. Please log in again.");
      setScreen("login");
    });
  }

  function handleMaskDone() {
    setMaskSelection(null);
    setScreen("browse");
  }

  if (screen === null) {
    return <div className="loading">Loading…</div>;
  }

  return (
    <div className="app">
      {error && (
        <div className="banner-error" onClick={() => setError(null)}>
          {error}
        </div>
      )}
      {screen === "login" ? (
        <Login onSuccess={() => setScreen("browse")} />
      ) : screen === "mask" && maskSelection ? (
        <MaskPanel
          key={maskSelection.playId}
          selection={maskSelection}
          onDone={handleMaskDone}
          onAuthExpired={handleAuthExpired}
        />
      ) : (
        <PlayBrowser onLogout={handleLogout} onAuthExpired={handleAuthExpired} />
      )}
    </div>
  );
}
